import React, { useEffect, useState, useContext } from 'react';

import AwaitingCreditRatingLayout from './awaiting-credit-rating-layout';
import {
    LoanApplicationStatus,
    LoanApplicationDeniedReason,
    useGetLoanQuery,
    useGetLeadStatisticLazyQuery
} from 'generated/graphql';
import {
    GenericErrorLayout,
    RequestDeniedLayout,
    RequestDeniedInQueueLayout,
    AnalysisTimeoutLayout,
} from '../error-message';
import { nextStep, setStep, Step } from 'actions/step.action';
import { PartnersListMode } from 'components/partners-list/partners-list.utils';
import useOptimizeConfig from 'components/optimize/optimize-hook';
import { registerPageView, registerTagmanagerEvent } from 'configs/trackers';
import { ProfileContext } from 'providers/profile.provider';
import { useProduct } from 'providers/product.provider';
import RequestDeniedLayoutPartners from '../error-message/request-denied-layout-partners';
import { useNavigation } from 'hooks/index';
import { LoanApplicationContext } from 'containers/context/useGetLoanApplication';

const ANALYSIS_TIMEOUT_IN_MILLISECONDS = 1000 * 60 * 2.5;
const AWAITING_TIME_TO_START_POLLER = 1000 * 30;

const AwaitingCreditRating: React.FC = () => {
    const {
        reset,
        goToLoanConfiguration,
        goToBizcapitalSite,
        goToWelcome
    } = useNavigation();
    const {
        showPartnersWithLines,
        showPartnerNavigationOnMobile,
    } = useOptimizeConfig();
    const { profile } = useContext(ProfileContext);

    const [maxPercentValue, setMaxPercentValue] = useState(94);
    const [isFinishedWithError, setFinishedWithError] = useState(false);
    const [isDenied, setDenied] = useState(false);
    const [isDeniedInQueue, setDeniedInQueue] = useState(false);
    const [analysisTimeout, setAnalysisTimeout] = useState(false);
    const { isPreApproved } = useProduct();
    const { loanApplicationId } = useContext(LoanApplicationContext);
    const [getLeadStatisticQuery] = useGetLeadStatisticLazyQuery();

    const response = useGetLoanQuery({
        variables: { id: loanApplicationId }
    });

    useEffect(() => {
        setTimeout(
            () => response.startPolling(5000),
            AWAITING_TIME_TO_START_POLLER
        );
        setTimeout(
            () => setAnalysisTimeout(true),
            ANALYSIS_TIMEOUT_IN_MILLISECONDS
        );

    }, []);

    useEffect(() => {
        try {
            const awaitProcessingStatus = () =>
                [
                    LoanApplicationStatus.Received,
                    LoanApplicationStatus.Draft,
                ].includes(response?.data?.loanApplication?.status);

            if (response?.data?.loanApplication && !awaitProcessingStatus()) {
                setMaxPercentValue(100);
                response.stopPolling();
            }
        } catch (error) {
            setFinishedWithError(true);
        }
    }, [response?.data?.loanApplication]);

    const registerConversionValue = async (
        loanApplicationId: string
    ): Promise<void> => {
        const response = await getLeadStatisticQuery({
            variables:{
                id: loanApplicationId
            }
        });

        if (response.data?.leadStatistic.conversionValue !== null) {
            registerTagmanagerEvent('calculate_conversion_value', {
                conversionValue: response.data.leadStatistic.conversionValue
            });
        } else {
            registerTagmanagerEvent('calculate_conversion_value', {
                conversionValue: 0,
            });
        }
    };

    const onComplete = () => {
        registerConversionValue(response.data.loanApplication.id);

        switch (response.data.loanApplication.status) {
            case LoanApplicationStatus.Awaiting:
                if (isPreApproved) {
                    goToWelcome();
                } else {
                    nextStep();
                    goToLoanConfiguration(response.data.loanApplication.id);
                }
                break;

            case LoanApplicationStatus.Denied:
                if (response.data.loanApplication.deniedReason === LoanApplicationDeniedReason.InQueue) {
                    registerPageView('pedido/analise/negado-em-fila');
                    setDeniedInQueue(true);
                    break;
                }
                registerPageView('pedido/analise/negado');
                setDenied(true);
                break;
            case LoanApplicationStatus.InAnalysis:
                if (isPreApproved) {
                    goToWelcome();
                }
                break;
            default:
                setFinishedWithError(true);
                break;
        }
    };

    if (isFinishedWithError) {
        setStep(Step.FLOW_FINISHED);

        return <GenericErrorLayout onOk={reset} />;
    }

    if (isDenied) {
        setStep(Step.FLOW_FINISHED);
        if (profile === "default") {
            return (
                <RequestDeniedLayout
                    screenProfile={profile}
                    partnersListProps={{
                        showArrows: showPartnerNavigationOnMobile,
                        mode: showPartnersWithLines
                            ? PartnersListMode.LINE
                            : PartnersListMode.COLUNM,
                    }}
                />
            );
        } else {
            return (
                <RequestDeniedLayoutPartners
                    screenProfile={profile}
                    partnersListProps={{
                        showArrows: showPartnerNavigationOnMobile,
                        mode: showPartnersWithLines
                            ? PartnersListMode.LINE
                            : PartnersListMode.COLUNM,
                    }}
                />
            );
        }
    }

    if (isDeniedInQueue) {
        setStep(Step.FLOW_FINISHED);

        return (
            <RequestDeniedInQueueLayout onOk={goToBizcapitalSite} />
        );
    }

    if (analysisTimeout) {
        registerPageView('pedido/analise/tempo-analise-excedido');
        setStep(Step.FLOW_FINISHED);

        return <AnalysisTimeoutLayout onOk={goToBizcapitalSite} />;
    }

    return (
        <AwaitingCreditRatingLayout
            estimatedLoadTimerInSeconds={100}
            screenProfile={profile}
            onComplete={onComplete}
            maxPercentValue={maxPercentValue}
        />
    );
};

export default AwaitingCreditRating;
